import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Save, Trash2, Folder, AlertTriangle, CheckCircle2, Star } from 'lucide-react';

/**
 * SaveTemplatesEditor — Settings panel for managing the
 * /api/save-templates collection. Operators set up folder hierarchy
 * patterns once here, then pick them on the Attachments tab when
 * uploading documents.
 */
const SCOPE_OPTIONS = ['appraisal', 'property', 'client', 'global'];

const TOKEN_REFERENCE = [
  { token: '{client_name}',       desc: "Client's full name (slugified — folder-safe)" },
  { token: '{client_id}',         desc: "Internal client ObjectId" },
  { token: '{client_no}',         desc: "Sequential client number (e.g. C1234)" },
  { token: '{property_address}',  desc: "Street + Suburb of the property" },
  { token: '{property_id}',       desc: "Sequential property number" },
  { token: '{appraisal_id}',      desc: "Internal appraisal ObjectId" },
  { token: '{appraisal_no}',      desc: "Sequential appraisal number" },
  { token: '{appraisal_type}',    desc: "e.g. Re-roof / Repairs" },
  { token: '{quotation_type}',    desc: "Quote type ref string" },
  { token: '{kind}',              desc: "Document kind tag (Supplier Quotation, Scanned Notes, …)" },
  { token: '{year}',              desc: "Upload year (YYYY)" },
  { token: '{month}',             desc: "Upload month (MM)" },
  { token: '{date}',              desc: "Upload date (YYYY-MM-DD)" },
  { token: '{timestamp}',         desc: "Upload time (YYYYMMDD-HHMMSS)" },
  { token: '{original_basename}', desc: "The original filename incl. extension" },
];

const BLANK_FORM = {
  name: '',
  scope: 'appraisal',
  folder_pattern: 'Client {client_name} - C{client_no}/{property_address} - P{property_id}/{kind}',
  filename_pattern: '{timestamp}_{original_basename}',
  notes: '',
  is_default: false,
};

export default function SaveTemplatesEditor({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [previewFor, setPreviewFor] = useState(null);  // template _id whose preview is open
  const [preview, setPreview] = useState(null);
  const [flash, setFlash] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editRowForm, setEditRowForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/save-templates');
      setRows(data || []);
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const previewTemplate = async (folder_pattern, filename_pattern, scope) => {
    try {
      const { data } = await api.post('/save-templates/preview', {
        scope, folder_pattern, filename_pattern,
        kind: 'Supplier Quotation',
        original_filename: 'invoice-12345.pdf',
      });
      setPreview(data);
    } catch (e) {
      setPreview({ folder: '(preview failed)', filename: e.message });
    }
  };

  const saveNew = async () => {
    if (!form.name.trim() || !form.folder_pattern.trim()) {
      setFlash({ tone: 'error', msg: 'Name + folder pattern required.' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/save-templates', form);
      setFlash({ tone: 'ok', msg: `Created "${form.name}"` });
      setForm(BLANK_FORM);
      setAdding(false);
      load();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingRowId(row._id);
    setEditRowForm({
      name: row.name,
      scope: row.scope,
      folder_pattern: row.folder_pattern,
      filename_pattern: row.filename_pattern || '{timestamp}_{original_basename}',
      notes: row.notes || '',
      is_default: !!row.is_default,
    });
  };

  const saveEdit = async (rowId) => {
    try {
      await api.put(`/save-templates/${rowId}`, editRowForm);
      setEditingRowId(null);
      setFlash({ tone: 'ok', msg: 'Template updated.' });
      load();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }
  };

  const deleteRow = async (row) => {
    if (row.is_seed) {
      setFlash({ tone: 'error', msg: 'Seed templates cannot be deleted — edit instead.' });
      return;
    }
    if (!window.confirm(`Delete template "${row.name}"?`)) return;
    try {
      await api.delete(`/save-templates/${row._id}`);
      setFlash({ tone: 'ok', msg: 'Template deleted.' });
      load();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-4" data-testid="save-templates-editor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 font-[Manrope]">
            <Folder size={18} className="text-orange-600" /> Save Templates
          </h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Define folder hierarchies and filename patterns once. They auto-apply whenever you
            upload a scan, supplier doc, or photo on a Client / Property / Appraisal page. One
            template per scope can be marked the <strong>default</strong> (marked with ★).
          </p>
        </div>
        <button
          data-testid="save-templates-add-btn"
          onClick={() => { setAdding(!adding); setForm(BLANK_FORM); }}
          className="text-sm bg-orange-600 text-white font-semibold rounded-md px-3 py-1.5 hover:bg-orange-700 inline-flex items-center gap-1"
        >
          <Plus size={14} /> {adding ? 'Cancel' : 'New template'}
        </button>
      </div>

      {flash && (
        <div data-testid="save-templates-flash" className={`text-sm rounded-md p-3 border ${flash.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
          {flash.msg}
        </div>
      )}

      {adding && (
        <div className="bg-orange-50/50 border border-orange-200 rounded-md p-4 space-y-3" data-testid="save-templates-new-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Template name</span>
              <input data-testid="new-tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" placeholder="e.g. Supplier docs (per appraisal)" />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Scope</span>
              <select data-testid="new-tpl-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm">
                {SCOPE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="block text-sm">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Folder pattern</span>
            <input data-testid="new-tpl-folder" value={form.folder_pattern} onChange={(e) => setForm({ ...form, folder_pattern: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono" />
          </label>
          <label className="block text-sm">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Filename pattern</span>
            <input data-testid="new-tpl-filename" value={form.filename_pattern} onChange={(e) => setForm({ ...form, filename_pattern: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono" />
          </label>
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="accent-orange-600" />
            Make this the default for the <strong>{form.scope}</strong> scope
          </label>
          <div className="flex justify-between items-center pt-2 border-t border-orange-200">
            <button
              type="button"
              onClick={() => previewTemplate(form.folder_pattern, form.filename_pattern, form.scope)}
              className="text-xs text-slate-600 hover:text-slate-900 hover:underline"
            >
              Preview path
            </button>
            <button data-testid="new-tpl-save" onClick={saveNew} disabled={saving} className="bg-orange-600 text-white text-sm font-semibold rounded-md px-3 py-1.5 hover:bg-orange-700 disabled:opacity-50 inline-flex items-center gap-1">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save template
            </button>
          </div>
          {preview && (
            <div className="bg-white border border-orange-300 rounded-md p-2 text-[11px] font-mono text-slate-700">
              <Folder size={11} className="inline mr-1 text-slate-400" />
              {preview.folder}/<span className="text-orange-700">{preview.filename}</span>
            </div>
          )}
        </div>
      )}

      {/* Token reference */}
      <details className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
        <summary className="cursor-pointer text-slate-700 font-semibold">Available tokens (click to expand)</summary>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
          {TOKEN_REFERENCE.map((t) => (
            <div key={t.token} className="flex gap-2">
              <code className="text-orange-700 font-mono w-44 flex-shrink-0">{t.token}</code>
              <span className="text-slate-600">{t.desc}</span>
            </div>
          ))}
        </div>
      </details>

      {/* Existing templates */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
          <Loader2 size={14} className="animate-spin" /> Loading templates…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-6 text-sm text-slate-500 text-center">No templates yet.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="save-templates-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-2 px-3 w-24">Scope</th>
                <th className="text-left p-2 px-3">Name</th>
                <th className="text-left p-2 px-3">Folder / Filename pattern</th>
                <th className="text-right p-2 px-3 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isEditing = editingRowId === row._id;
                return (
                  <tr key={row._id} data-testid={`tpl-row-${row._id}`} className="border-t border-slate-100">
                    <td className="p-2 px-3">
                      <span className="text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                        {row.scope}
                      </span>
                    </td>
                    <td className="p-2 px-3">
                      {isEditing ? (
                        <input value={editRowForm.name} onChange={(e) => setEditRowForm({ ...editRowForm, name: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
                      ) : (
                        <div>
                          <div className="font-semibold text-slate-800 inline-flex items-center gap-1">
                            {row.name}
                            {row.is_default && <Star size={12} className="text-amber-500 fill-amber-400" />}
                          </div>
                          {row.notes && <div className="text-[11px] text-slate-500 italic">{row.notes}</div>}
                        </div>
                      )}
                    </td>
                    <td className="p-2 px-3 font-mono text-[11px]">
                      {isEditing ? (
                        <div className="space-y-1">
                          <input value={editRowForm.folder_pattern} onChange={(e) => setEditRowForm({ ...editRowForm, folder_pattern: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono" placeholder="folder pattern" />
                          <input value={editRowForm.filename_pattern} onChange={(e) => setEditRowForm({ ...editRowForm, filename_pattern: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono" placeholder="filename pattern" />
                          <label className="text-[11px] text-slate-600 flex items-center gap-1">
                            <input type="checkbox" checked={editRowForm.is_default} onChange={(e) => setEditRowForm({ ...editRowForm, is_default: e.target.checked })} className="accent-orange-600" />
                            Set as default for the {editRowForm.scope} scope
                          </label>
                        </div>
                      ) : (
                        <div>
                          <div className="text-slate-700">{row.folder_pattern}</div>
                          <div className="text-slate-500">{row.filename_pattern}</div>
                        </div>
                      )}
                    </td>
                    <td className="p-2 px-3 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button onClick={() => saveEdit(row._id)} className="text-[11px] text-emerald-700 hover:underline mr-2">Save</button>
                          <button onClick={() => setEditingRowId(null)} className="text-[11px] text-slate-500 hover:underline">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button data-testid={`tpl-preview-${row._id}`} onClick={() => { setPreviewFor(row._id); previewTemplate(row.folder_pattern, row.filename_pattern, row.scope); }} className="text-[11px] text-sky-700 hover:underline mr-2">Preview</button>
                          <button onClick={() => startEdit(row)} className="text-[11px] text-slate-600 hover:underline mr-2">Edit</button>
                          {!row.is_seed && (
                            <button onClick={() => deleteRow(row)} className="text-[11px] text-rose-600 hover:underline inline-flex items-center gap-0.5">
                              <Trash2 size={11} /> Delete
                            </button>
                          )}
                        </>
                      )}
                      {previewFor === row._id && preview && (
                        <div className="text-[11px] mt-1 bg-slate-50 border border-slate-200 rounded p-1.5 font-mono text-left">
                          {preview.folder}/<span className="text-orange-700">{preview.filename}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
